<template>
  <div class="invoice-detail-page">
    <navigation>发票详情</navigation>

    <div class="page-content" v-if="!loading">
      <!-- 状态头部 -->
      <div class="status-header" :class="statusClass">
        <div class="status-header-circle"></div>
        <div class="status-header-content">
          <div class="status-icon">
            <!-- 开票中 -->
            <svg v-if="detail.status === 'ING'" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
              <path d="M32 18v18l12 7" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <!-- 开票成功 -->
            <svg v-else-if="detail.status === 'SUCCESS'" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
              <path d="M20 33l8 8 16-18" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <!-- 开票失败 -->
            <svg v-else viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
              <path d="M24 24l16 16M40 24l-16 16" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round"/>
            </svg>
          </div>
          <p class="status-title">{{ detail.statusText }}</p>
          <p class="status-desc">{{ statusDesc }}</p>
          <p class="status-amount" v-if="detail.status === 'SUCCESS'">开票金额：￥{{ detail.invoiceAmount }}</p>
        </div>
      </div>

      <!-- 申请信息 -->
      <div class="info-card">
        <div class="card-title">申请信息</div>
        <nd-cell class="bill-no-cell" title="借据编号" :text="detail.billNo" :arrow="false" />
        <nd-cell title="资方名称" :text="detail.fundName" :arrow="false" />
        <nd-cell title="发票抬头" :text="detail.invoiceTitle" :arrow="false" />
        <nd-cell title="申请时间" :text="detail.applyTime" :arrow="false" />
        <nd-cell title="更新时间" :text="detail.bizUpdateTime" :arrow="false" />
        <div class="amount-row">
          <span class="amount-label">开票金额 (元)</span>
          <span class="amount-value">￥{{ detail.invoiceAmount }}</span>
        </div>
      </div>

      <!-- 发票文件 -->
      <div class="file-card" v-if="isCompleted && detail.invoiceFileList && detail.invoiceFileList.length">
        <div class="card-title">发票文件</div>
        <div class="file-item" v-for="(file, index) in detail.invoiceFileList" :key="index" @click="previewImage(file.invoiceFile)">
          <div class="file-info">
            <svg class="file-icon" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="2" width="32" height="36" rx="4" fill="#FFF1F0"/>
              <path d="M12 16h16M12 22h16M12 28h10" stroke="#FF4633" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="file-name">电子发票{{ index + 1 }}</span>
          </div>
          <span class="file-amt">￥{{ formatAmount(file.invoiceAmt) }}</span>
          <svg class="file-arrow" viewBox="0 0 16 28" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3l11 11-11 11" stroke="#CCCCCC" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="file-tip">点击查看发票，长按图片可保存到相册</p>
      </div>

      <!-- 部分开票失败 -->
      <div class="fail-section" v-if="detail.invoiceFailList && detail.invoiceFailList.length">
        <div class="fail-banner">
          <span class="fail-banner-icon">!</span>
          <span>部分发票金额开票失败，请联系相关主体处理</span>
        </div>
        <div class="fail-card" v-for="(item, index) in detail.invoiceFailList" :key="index">
          <nd-cell title="失败金额" :text="'￥' + formatAmount(item.failAmt)" :arrow="false" />
          <nd-cell title="主体名称" :text="item.creditorMain" :arrow="false" />
          <div class="fail-phone-row" @click="callPhone(item.creditorPhone)">
            <span class="fail-phone-label">联系电话</span>
            <span class="fail-phone-value">{{ item.creditorPhone }}</span>
            <svg class="fail-phone-arrow" viewBox="0 0 16 28" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3l11 11-11 11" stroke="#1890FF" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- 底部客服 -->
      <div class="footer-tip">
        如有疑问，请联系客服热线：<a href="tel:4000100008">4000100008</a>
      </div>
    </div>

    <!-- 长按保存提示 -->
    <transition name="tip-fade">
      <div class="save-tip" v-if="showSaveTip">长按图片保存到相册</div>
    </transition>
  </div>
</template>

<script>
import { ImagePreview } from 'vant'
import { mapActions } from 'vuex'

export default {
  name: 'InvoiceDetail',

  data () {
    return {
      loading: true,
      detail: {},
      showSaveTip: false,
      tipTimer: null
    }
  },

  computed: {
    isCompleted () {
      return this.detail.status === 'SUCCESS'
    },
    statusClass () {
      const map = { ING: 'processing', SUCCESS: 'completed', FAILED: 'failed' }
      return map[this.detail.status] || ''
    },
    statusDesc () {
      const map = {
        ING: '发票正在开具中，请耐心等待',
        SUCCESS: '您的电子发票已开具成功',
        FAILED: '开票失败，请联系客服处理'
      }
      return map[this.detail.status] || ''
    }
  },

  created () {
    this.fetchDetail()
    this.$bus.$emit('ready')
  },

  beforeDestroy () {
    clearTimeout(this.tipTimer)
  },

  methods: {
    ...mapActions('invoice', ['getInvoiceDetailX']),

    async fetchDetail () {
      try {
        const applyNo = this.$route.query.applyNo
        if (!applyNo) return
        const res = await this.getInvoiceDetailX({ applyNo })
        if (res.code === '000000' && res.data) {
          const d = res.data
          this.detail = {
            billNo: d.billNo,
            fundName: d.fundName,
            invoiceTitle: d.invoiceTitle,
            applyTime: d.applyTime,
            bizUpdateTime: d.bizUpdateTime,
            invoiceAmount: this.formatAmount(d.applyInvoiceAmt),
            status: d.status,
            statusText: this.statusText(d.status),
            invoiceFileList: d.invoiceFileList || [],
            invoiceFailList: d.invoiceFailList || []
          }
        }
      } catch (e) {
        console.error('获取发票详情失败', e)
      } finally {
        this.loading = false
        this.$bus.$emit('ready')
      }
    },

    formatAmount (val) {
      if (val == null) return '0.00'
      return Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    statusText (status) {
      const map = { ING: '开票处理中', SUCCESS: '开票成功', FAILED: '开票失败' }
      return map[status] || ''
    },

    previewImage (url) {
      ImagePreview({
        images: [url],
        closeable: true,
        onClose: () => {
          clearTimeout(this.tipTimer)
          this.showSaveTip = false
        }
      })
      this.showSaveTip = true
      this.tipTimer = setTimeout(() => {
        this.showSaveTip = false
      }, 3000)
    },

    callPhone (phone) {
      if (phone) window.location.href = 'tel:' + phone
    }
  }
}
</script>

<style lang="less" scoped>
@import "../../assets/less/mixin.less";

.invoice-detail-page {
  min-height: 100vh;
  background-color: #F7F7F7;
  padding-bottom: 40px;
}

.page-content {
  padding: 0 30px;
}

/* 状态头部 */
.status-header {
  position: relative;
  margin: 0 -30px;
  padding: 50px 40px 60px;
  border-radius: 0 0 40px 40px;
  overflow: hidden;
  text-align: center;

  &.processing {
    background: linear-gradient(135deg, #FF9F18 0%, #FFB84D 100%);
  }

  &.completed {
    background: linear-gradient(135deg, #FF4633 0%, #FF7B54 100%);
  }

  &.failed {
    background: linear-gradient(135deg, #FF4D4F 0%, #FF7875 100%);
  }

  &-circle {
    position: absolute;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    top: -80px;
    right: -40px;
  }

  &-content {
    position: relative;
    z-index: 1;
  }
}

.status-icon {
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;

  svg {
    width: 120px;
    height: 120px;
  }
}

.status-title {
  font-size: 40px;
  font-family: PingFangSC-Semibold, PingFang SC;
  font-weight: 600;
  color: #FFFFFF;
  line-height: 56px;
  margin-bottom: 12px;
}

.status-desc {
  font-size: 26px;
  font-family: PingFangSC-Regular, PingFang SC;
  color: rgba(255, 255, 255, 0.85);
  line-height: 36px;
}

.status-amount {
  font-size: 32px;
  font-family: PingFangSC-Medium, PingFang SC;
  font-weight: 500;
  color: #FFFFFF;
  line-height: 44px;
  margin-top: 20px;
}

/* 通用卡片 */
.info-card,
.file-card,
.fail-card {
  background: #FFFFFF;
  border-radius: 20px;
  margin-top: 24px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04);
}

.card-title {
  font-size: 30px;
  font-family: PingFangSC-Medium, PingFang SC;
  font-weight: 500;
  color: #333333;
  padding: 28px 24px 8px;
}

/* 金额行 */
.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 28px;
  border-top: 1px solid #F5F5F5;
  margin-top: 8px;

  .amount-label {
    font-size: 26px;
    color: #999999;
    font-family: PingFangSC-Regular, PingFang SC;
  }

  .amount-value {
    font-size: 32px;
    color: #FF4633;
    font-weight: 600;
    font-family: DIN-Medium, DIN, PingFangSC-Semibold, PingFang SC;
  }
}

/* 发票文件 */
.file-item {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #F5F5F5;

  &:last-of-type {
    border-bottom: none;
  }

  &:active {
    background: #FAFAFA;
  }
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.file-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-right: 16px;
}

.file-name {
  font-size: 26px;
  color: #333333;
  font-family: PingFangSC-Regular, PingFang SC;
}

.file-amt {
  font-size: 24px;
  color: #999999;
  font-family: PingFangSC-Regular, PingFang SC;
  margin: 0 16px;
  flex-shrink: 0;
}

.file-arrow {
  width: 14px;
  height: 24px;
  flex-shrink: 0;
}

.file-tip {
  font-size: 22px;
  color: #CCCCCC;
  font-family: PingFangSC-Regular, PingFang SC;
  text-align: center;
  padding: 16px 0 24px;
}

/* 开票失败 */
.fail-section {
  margin-top: 24px;
}

.fail-banner {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: #FFF8F0;
  border-radius: 16px;
  margin-bottom: 16px;
  font-size: 24px;
  color: #996633;
  font-family: PingFangSC-Regular, PingFang SC;
  line-height: 36px;

  &-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    border-radius: 50%;
    background: #FF9F18;
    color: #fff;
    font-size: 22px;
    font-weight: bold;
    margin-right: 12px;
  }
}

.fail-phone-row {
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: 110px;

  &:active {
    background: #FAFAFA;
  }

  .fail-phone-label {
    font-size: 30px;
    color: #333333;
    font-family: PingFangSC-Regular, PingFang SC;
    flex-shrink: 0;
  }

  .fail-phone-value {
    font-size: 30px;
    color: #1890FF;
    font-family: PingFangSC-Regular, PingFang SC;
    margin-left: auto;
    padding-right: 12px;
  }

  .fail-phone-arrow {
    width: 14px;
    height: 24px;
    flex-shrink: 0;
  }
}

/* 底部客服 */
.footer-tip {
  text-align: center;
  font-size: 24px;
  color: #999999;
  font-family: PingFangSC-Regular, PingFang SC;
  padding: 40px 0 20px;

  a {
    color: #FF4633;
    font-weight: 500;
    text-decoration: none;
  }
}

/* 长按保存提示 */
.save-tip {
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 26px;
  padding: 12px 32px;
  border-radius: 32px;
  z-index: 3000;
  font-family: PingFangSC-Regular, PingFang SC;
  white-space: nowrap;
}

/* 借据编号过长处理 */
.bill-no-cell {
  /deep/ .nd-cell-header .container:first-child {
    flex-shrink: 0;
  }
  /deep/ .nd-cell-header .container:last-child {
    flex-shrink: 1;
    min-width: 0;
    justify-content: flex-end;
    padding-left: 40px;
  }
  /deep/ .nd-cell-header .text {
    text-align: right;
    word-break: break-all;
  }
}

.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: opacity 0.5s ease;
}

.tip-fade-enter,
.tip-fade-leave-to {
  opacity: 0;
}
</style>
